/**
 * Écran de connexion.
 *
 * Gère les deux cas du parcours réel : la connexion ordinaire, et le changement de mot de passe imposé
 * à la première connexion — Cognito envoie une invitation avec un mot de passe temporaire
 * (`docs/agent-spec/06-authentification-et-droits.md`). Traiter ce second cas comme un échec
 * bloquerait tout nouvel utilisateur dès son premier accès.
 */
import { useState } from "react";

import { MotDePasseAChanger, definirNouveauMotDePasse } from "../../shared/auth/cognito";
import { useAuth } from "../../shared/auth/contexte";

/**
 * Politique du pool Cognito, recopiée pour donner un retour immédiat.
 *
 * Duplication assumée et bornée : Cognito refuse de toute façon, cette vérification n'existe que pour
 * éviter un aller-retour réseau et un message technique. Elle ne remplace rien
 * (`docs/agent-spec/20-formulaires.md` : la validation côté client est un confort).
 */
const LONGUEUR_MINIMALE = 12;

function motDePasseConforme(valeur: string): string | null {
  if (valeur.length < LONGUEUR_MINIMALE) {
    return `Le mot de passe doit compter au moins ${LONGUEUR_MINIMALE} caractères.`;
  }
  if (!/[a-z]/.test(valeur)) return "Il doit contenir au moins une minuscule.";
  if (!/[A-Z]/.test(valeur)) return "Il doit contenir au moins une majuscule.";
  if (!/\d/.test(valeur)) return "Il doit contenir au moins un chiffre.";
  if (!/[^\w\s]/.test(valeur)) return "Il doit contenir au moins un caractère spécial.";
  return null;
}

export function EcranConnexion() {
  const { seConnecter, definirIdentite } = useAuth();

  const [email, definirEmail] = useState("");
  const [motDePasse, definirMotDePasse] = useState("");
  const [nouveauMotDePasse, definirNouveau] = useState("");
  const [aChanger, definirAChanger] = useState<MotDePasseAChanger | null>(null);
  const [message, definirMessage] = useState<string | null>(null);
  const [enCours, definirEnCours] = useState(false);

  async function soumettre(evenement: React.FormEvent) {
    evenement.preventDefault();
    definirMessage(null);
    definirEnCours(true);
    try {
      if (aChanger) {
        const probleme = motDePasseConforme(nouveauMotDePasse);
        if (probleme) {
          definirMessage(probleme);
          return;
        }
        definirIdentite(await definirNouveauMotDePasse(aChanger.utilisateur, nouveauMotDePasse));
        return;
      }
      await seConnecter(email, motDePasse);
    } catch (erreur) {
      if (erreur instanceof MotDePasseAChanger) {
        definirAChanger(erreur);
        definirMessage(
          "Première connexion : choisissez un mot de passe personnel pour continuer.",
        );
        return;
      }
      // Le message de Cognito est repris tel quel : il distingue un compte désactivé d'un mot de
      // passe erroné, ce qu'un texte générique masquerait.
      definirMessage(erreur instanceof Error ? erreur.message : "La connexion a échoué.");
    } finally {
      definirEnCours(false);
    }
  }

  return (
    <div className="connexion">
      <form className="connexion__carte" onSubmit={soumettre}>
        <h1>Dashboard GPS - GRV JET A-1</h1>
        <p className="connexion__sous-titre">Total Energies</p>

        <label htmlFor="email">Adresse email</label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          disabled={aChanger !== null}
          onChange={(e) => definirEmail(e.target.value)}
        />

        {aChanger === null ? (
          <>
            <label htmlFor="mot-de-passe">Mot de passe</label>
            <input
              id="mot-de-passe"
              type="password"
              autoComplete="current-password"
              required
              value={motDePasse}
              onChange={(e) => definirMotDePasse(e.target.value)}
            />
          </>
        ) : (
          <>
            <label htmlFor="nouveau-mot-de-passe">Nouveau mot de passe</label>
            <input
              id="nouveau-mot-de-passe"
              type="password"
              autoComplete="new-password"
              required
              value={nouveauMotDePasse}
              onChange={(e) => definirNouveau(e.target.value)}
            />
            <small>
              Au moins {LONGUEUR_MINIMALE} caractères, avec majuscule, minuscule, chiffre et caractère
              spécial.
            </small>
          </>
        )}

        {message && (
          <p className="connexion__message" role="alert">
            {message}
          </p>
        )}

        <button type="submit" disabled={enCours}>
          {enCours ? "Connexion…" : aChanger ? "Valider le mot de passe" : "Se connecter"}
        </button>
      </form>
    </div>
  );
}
